// cms-core.js
import { useState, useEffect, useRef } from "react"
import CMSController from "./controllers/cms-controller"
import { usePromise } from "aldoo-ra/usePromise"
import { absoluteURLFields } from "./url"
import useCMSLocalization from "./cms-localization"
import Localization from "aldoo-ra/Localization"
import { usePageContext } from "vike-react/usePageContext"
import { ssrCMSKey } from "./ssr-cms-key"

const cache = {}

/**
 * Clear CMS cache for a specific content type or all cache
 * @param {string} contentType - Optional content type to clear, if not provided clears all
 */
export const clearCMSCache = (contentType) => {
  if (contentType) {
    // Clear only entries for specific content type
    Object.keys(cache).forEach(key => {
      if (key.startsWith(`${contentType}:`)) {
        delete cache[key]
      }
    })
  } else {
    // Clear entire cache
    Object.keys(cache).forEach(key => delete cache[key])
  }
}

const CMSData = ({
  contentType,
  match,
  sort,
  itemsPerPage = 1000,
  page = 1,
  onData,
  manual = false,
  expand = false,
}) => {
  const [isClient, setIsClient] = useState(false)
  const [payload, setPayload] = useState(null)
  const loading = useRef(false)
  const lastMatch = useRef(null)
  const [error, setError] = useState(null)
  const { getContent } = CMSController()
  const { readLocalization, resolveLocalization } = useCMSLocalization()
  const { currentLanguage } = Localization()
  
  // Get SSR language from pageContext when available (server-side) 
  let pageContext
  try {
    pageContext = usePageContext()
  } catch {
    // usePageContext not available (e.g., in tests or certain contexts)
    pageContext = null
  }
  
  // Use SSR language during server-side rendering, fallback to Localization hook
  const effectiveLanguage = (typeof window === 'undefined' && pageContext?.ssrLanguage) || currentLanguage || 'en'

  // Handle client/server difference
  useEffect(() => {
    setIsClient(true)
  }, [])

  const fetchContent = async () => {
    if (loading.current) return
    try {
      loading.current = true
      // Generate cache key for local caching
      const key = `${contentType}:${JSON.stringify(match)}:${sort}:${itemsPerPage}:${page}:${effectiveLanguage}:${expand}`

      // Check local cache first
      let cmsContent = cache[key]
      
      // If no cached data, make API call
      if (!cmsContent) {
        const queryMatch = { ...match, "content.type": contentType }
        const query = {
          match: queryMatch,
        }
        
        // Only include pagination parameters if they differ from "get all" defaults
        if (page !== 1) {
          query.page = page
        }
        if (itemsPerPage !== 1000) {
          query.itemsPerPage = itemsPerPage
        }
        
        // Only include sort if explicitly provided
        if (sort) {
          query.sort = sort
        }

        if (expand) {
          query.expand = true
        }

        cmsContent = await getContent(query)
        
        // Cache the result
        if (cmsContent && !cmsContent.error) {
          cache[key] = cmsContent
        }
      }

      if (cmsContent?.data) {
        await readLocalization(cmsContent.data)
        setPayload(cmsContent)
        if (onData) {
          onData({
            data: cmsContent.data,
            totalPages: cmsContent.totalPages,
          })
        }
      } else {
        setError("No content available")
      }
    } catch (err) {
      setError("Error fetching CMS content")
      console.error(err)
    } finally {
      loading.current = false
    }
  }

  const { execute, pending, reset } = usePromise(fetchContent, manual)

  useEffect(() => {
    if (match && JSON.stringify(lastMatch.current) === JSON.stringify(match))
      return
    if (loading.current) return
    if (pending) return

    lastMatch.current = match
    if (reset) reset()
    if (!manual) execute()
  }, [match])

  useEffect(() => {
    if (loading.current) return
    if (pending) return
    if (execute && !manual) execute()
  }, [page])

  // Refetch when language changes
  useEffect(() => {
    if (loading.current) return
    if (pending) return
    if (execute && !manual && isClient) execute()
  }, [effectiveLanguage, isClient])

  // Rendered with the page (./ssr-cms.js): on the server the query is recorded
  // for the next pass, and the server and the first client render answer
  // from what that pass fetched, in the language the page was rendered in.
  // In the browser the copy stands until the component's own fetch answers,
  // as long as the language is still the one it was rendered in.
  const ssrKey = ssrCMSKey({ contentType, match, sort, itemsPerPage, page, expand }, pageContext?.ssrLanguage || "en")
  // a query with a value still unknown on the server (an id the browser
  // works out) is not rendered with the page: it would ask for something else
  const ssrable = !match || Object.values(match).every((value) => value !== undefined)
  if (ssrable && !isClient && !manual && typeof window === "undefined" && pageContext?._ssrCMSWanted && !pageContext.ssrCMS?.[ssrKey]) {
    pageContext._ssrCMSWanted.set(ssrKey, { contentType, match, sort, itemsPerPage, page, expand })
  }
  const rendered = ssrable && !manual && !payload && (!isClient || effectiveLanguage === (pageContext?.ssrLanguage || "en")) ? pageContext?.ssrCMS?.[ssrKey] : null
  if (rendered) {
    return {
      data: rendered.data,
      loading: false,
      error: null,
      totalPages: rendered.totalPages,
      refresh: execute,
      isClient,
    }
  }

  // During SSR, return loading state - data will be fetched via API
  if (!isClient) {
    return {
      data: [],
      loading: true,
      error: null,
      totalPages: 0,
      refresh: execute,
      isClient: false,
    }
  }

  // On server-side, execute immediately to get data
  if (!isClient && !payload && !loading.current && !manual) {
    fetchContent()
  }

  const processedData = payload?.data
    ? payload.data.map(resolveLocalization).map(item => absoluteURLFields(item))
    : []

  // Show loading state appropriately
  const shouldShowLoading = loading.current || pending

  return {
    data: processedData,
    loading: shouldShowLoading,
    error,
    totalPages: payload?.totalPages || 0,
    refresh: execute,
    isClient,
  }
}

export default CMSData
